<template>
    <new-modal
        ref="studentQuestionOfTheDayModal"
        :show-close-x="false"
        size="4xl"
        v-on:modal-opened="onModalOpened()"
    >
        <template #trigger>
            <ul>
                <li
                    class="leading-0 flex w-[290px] items-center gap-2 px-4 py-2 text-[16px] text-gray-925 hover:cursor-pointer hover:bg-white/5 lg:text-white"
                >
                    <div class="flex items-center gap-2">
                        <div class="hidden md:block">Question of the Day</div>
                        <div class="block md:hidden">Question of the Day</div>

                        <svg
                            v-if="!questionAnswered"
                            width="19"
                            height="19"
                            viewBox="0 0 19 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="9.5" cy="9.5" r="7.5" fill="#407c7d" />
                            <circle cx="9.5" cy="9.5" r="3.5" fill="#01CECD" />
                        </svg>
                    </div>
                </li>
            </ul>
        </template>
        <template #header>
            <div class="flex w-full justify-between">
                <div class="pr-12 font-semibold">
                    <div class="hidden md:block">Question of the Day</div>
                    <div class="block md:hidden">QOTD</div>
                </div>
                <div class="text-gray-700">
                    {{ dayjs().utc(false).format("ddd, MMM D, YYYY") }}
                </div>
            </div>
        </template>
        <template #content>
            <div class="mb-4 text-[16px]">
                <span v-html="studentQuestionOfTheDay.question.content" />
            </div>
            <div v-if="!questionAnswered" class="text-[16px]">
                <ul>
                    <li
                        v-for="(answer, index) in answers"
                        @click="selectAnswer(answer)"
                        :class="
                            'mb-3 rounded border  p-4  hover:cursor-pointer' +
                            ' ' +
                            (answer.selected
                                ? 'border-primary-525 bg-primary-525 text-white'
                                : 'border-gray-325 text-gray-700 hover:bg-gray-325')
                        "
                    >
                        <div class="flex justify-start gap-4">
                            <div
                                class="h-8 w-8 rounded border border-inherit p-1 text-center text-[14px] font-medium"
                            >
                                {{ answerLabels[index] }}
                            </div>
                            <div class="">{{ answer.text }}</div>
                        </div>
                    </li>
                </ul>
            </div>

            <div v-else>
                <ul class="text-[14px]">
                    <li
                        v-for="(answer, index) in answers"
                        :class="
                            'relative mb-3 rounded border' +
                            ' ' +
                            (answer.is_correct
                                ? '!border-green-500 !bg-white !text-gray-700'
                                : '') +
                            ' ' +
                            (!answer.is_correct &&
                            selectedAnswerId === answer.id
                                ? '!border-red-500 !bg-white !text-gray-700'
                                : '')
                        "
                    >
                        <div
                            :class="
                                'absolute h-full' +
                                ' ' +
                                (answer.is_correct
                                    ? 'bg-green-500 opacity-20'
                                    : 'bg-gray-600 opacity-20') +
                                ' ' +
                                (!answer.is_correct &&
                                selectedAnswerId === answer.id
                                    ? '!bg-red-500  opacity-20'
                                    : '') +
                                ' ' +
                                (getAnswerFrequencyPercent(answer.id) < 100
                                    ? 'rounded-l'
                                    : 'rounded')
                            "
                            :style="
                                'width:' +
                                getAnswerFrequencyPercent(answer.id) +
                                '%'
                            "
                        ></div>
                        <div class="flex justify-start gap-4 p-4">
                            <div
                                class="h-8 w-8 rounded border border-inherit p-1 text-center text-[14px] font-medium"
                            >
                                {{ answerLabels[index] }}
                            </div>
                            <div class="">
                                {{ answer.text }}
                            </div>

                            <div
                                class="ml-auto flex w-36 flex-none flex-col items-center justify-center gap-0 whitespace-nowrap"
                            >
                                <div
                                    v-if="answer.is_correct"
                                    class="text-green-500"
                                >
                                    Correct Answer
                                </div>
                                <div
                                    v-if="
                                        !answer.is_correct &&
                                        selectedAnswerId === answer.id
                                    "
                                    class="flex items-center gap-2 text-red-500"
                                >
                                    <div class="">Your Answer</div>
                                </div>
                                <div
                                    class="flex items-center whitespace-nowrap text-[14px] italic text-gray-700 opacity-75"
                                >
                                    ({{ getAnswerFrequencyPercent(answer.id) }}%
                                    of responses)
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>

                <div
                    v-if="studentQuestionOfTheDay.question.answer_rationale"
                    class="bg-gray-200 p-4"
                >
                    <div
                        :class="
                            'flex flex-col items-start gap-4 lg:grid' +
                            ' ' +
                            (studentQuestionOfTheDay.question
                                .video_answer_rationale
                                ? 'lg:grid-cols-2'
                                : 'lg:grid-cols-1')
                        "
                    >
                        <div class="">
                            <div class="mb-2 text-[16px] font-semibold">
                                Explanation
                            </div>
                            <div
                                class="text-[16px] leading-loose text-gray-700"
                            >
                                {{
                                    studentQuestionOfTheDay.question
                                        .answer_rationale
                                }}
                            </div>
                        </div>
                        <div
                            class="aspect-video w-full lg:aspect-auto lg:h-auto lg:w-auto"
                            v-if="
                                studentQuestionOfTheDay.question
                                    .video_answer_rationale
                            "
                        >
                            <video-player
                                :video-token="
                                    studentQuestionOfTheDay.question
                                        .video_answer_rationale
                                "
                            ></video-player>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #controls>
            <div
                v-if="!questionAnswered"
                class="mt-4 flex justify-center gap-4"
            >
                <div
                    @click="closeModal"
                    class="rounded border border-gray-325 px-3 py-2 hover:cursor-pointer hover:bg-gray-325"
                >
                    Cancel
                </div>
                <div
                    v-if="selectedAnswerId == null"
                    class="rounded border border-primary-525 bg-primary-525 px-3 py-2 text-white opacity-50 hover:cursor-not-allowed"
                >
                    Submit
                </div>
                <div
                    v-else
                    @click="submit"
                    class="rounded border border-primary-525 bg-primary-525 px-3 py-2 text-white hover:cursor-pointer hover:bg-primary-600"
                >
                    Submit
                </div>
            </div>
            <div v-else class="flex justify-center gap-4">
                <div
                    @click="closeModal"
                    class="rounded border border-gray-325 px-3 py-2 hover:cursor-pointer hover:bg-gray-325"
                >
                    Close
                </div>
            </div>
        </template>
    </new-modal>
</template>
<script>
import NewModal from "@/Components/NewModal.vue";
import VideoPlayer from "@/Components/VideoPlayer.vue";
import { Inertia } from "@inertiajs/inertia";

export default {
    name: "StudentQuestionOfTheDayModal",
    components: { VideoPlayer, NewModal },
    props: {
        studentQuestionOfTheDay: {},
    },
    data() {
        return {
            answers: this.studentQuestionOfTheDay.question.answers,
            answerLabels: ["A", "B", "C", "D"],
            selectedAnswerId: null,
            allStudentAnswers: this.studentQuestionOfTheDay.all_student_answers,
            questionAnswered:
                this.studentQuestionOfTheDay.student_answer !== null,
        };
    },
    methods: {
        selectAnswer(answer) {
            this.answers.forEach(function (answer) {
                answer.selected = false;
            });

            answer.selected = true;
            this.selectedAnswerId = answer.id;
        },
        submit() {
            let vm = this;

            axios
                .post("/student/question-of-the-day/submit-answer", {
                    question_of_the_day_id: vm.studentQuestionOfTheDay.id,
                    answer_id: vm.selectedAnswerId,
                })
                .then(function (response) {
                    vm.questionAnswers = response.data.question.answers;
                    vm.allStudentAnswers = response.data.all_student_answers;
                    vm.questionAnswered = true;
                    vm.$page.props.studentQotdAvailable = false;
                });
        },
        onModalOpened() {
            this.$page.props.qotdModalOpen = true;
            localStorage.setItem("studentQotdShownAt", Date.now());
            localStorage.setItem(
                "studentQotdShownId",
                this.studentQuestionOfTheDay.id
            );
        },
        closeModal() {
            this.$page.props.qotdModalOpen = false;
            this.$refs.studentQuestionOfTheDayModal.closeModal();
        },
        getAnswerFrequencyPercent(answerId) {
            let frequencyPercent = 0;

            if (
                this.allStudentAnswers &&
                this.allStudentAnswers.count_by_answer_id[answerId]
            ) {
                frequencyPercent = Math.round(
                    (this.allStudentAnswers.count_by_answer_id[answerId] /
                        this.allStudentAnswers.total_attempts) *
                        100
                );
            }

            return frequencyPercent;
        },
        checkShouldModalOpen() {
            if (
                this.studentQuestionOfTheDay.questionAnswered ||
                this.$page.props.qotdModalOpen ||
                route().current() !== "student.dashboard" ||
                this.$page.props.auth.user.subscription.status !== "active"
            ) {
                return false;
            }

            let openModal = false;
            let hasSeenQotdRecently = true;
            let lastSeenQotdId = parseInt(
                localStorage.getItem("studentQotdShownId")
            );

            if (
                !localStorage.getItem("studentQotdShownAt") ||
                localStorage.getItem("studentQotdShownAt") <=
                    Date.now() - 1000 * 60 * 60 * 6
            ) {
                hasSeenQotdRecently = false;
            }

            if (
                lastSeenQotdId !== this.studentQuestionOfTheDay.id ||
                (this.$page.props.studentQotdAvailable && !hasSeenQotdRecently)
            ) {
                openModal = true;
            }

            return openModal;
        },
    },
    mounted() {
        Inertia.on("success", (event) => {
            if (
                this.$refs.studentQuestionOfTheDayModal &&
                this.checkShouldModalOpen()
            ) {
                this.$refs.studentQuestionOfTheDayModal.openModal();
            }
        });
    },
};
</script>
